import { useAuthStore } from '@/stores/auth-store'
import { useBidsStore } from '@/stores/bids-store'

/** The one line shown wherever a write or AI control is withheld from a viewer. */
export const VIEW_ONLY_MESSAGE = 'You have view-only access'

/**
 * The line shown to an organisation member who is not on the bid. Unlike a
 * viewer, being added to the bid gives them edit access, so it says how.
 */
export const BID_READ_ONLY_MESSAGE =
  'You’re viewing this bid in read-only mode. Ask a bid owner to add you for edit access.'

/**
 * True for an organisation viewer. The backend refuses every write from this
 * role (`withAuth` rejects POST/PUT/PATCH/DELETE with a 403), so write and AI
 * controls are hidden for them and editors open read-only — offering a control
 * that can only fail silently is worse than not offering it.
 *
 * Use this instead of reading `role === 'viewer'` inline, so every surface
 * answers the question the same way.
 *
 * Inside a bid, most controls want {@link useBidReadOnly} instead: it also
 * covers a member who is not on the bid. Keep this one for the few POSTs a
 * non-member may still make — exporting, saving an outline as a template.
 */
export function useIsViewOnly(): boolean {
  return useAuthStore((s) => s.role === 'viewer')
}

/**
 * Whether `role` can be trusted yet. The shell renders before the profile
 * loads, with `role` at its 'member' default, so a viewer briefly looks like a
 * member. Anything that would open an AI session — refused to a viewer —
 * waits for this, or it fires a request that can only 403.
 */
export function useIsRoleLoaded(): boolean {
  // `!== false`: a store (or test double) without the flag counts as loaded.
  return useAuthStore((s) => s.isRoleLoaded !== false)
}

export interface BidReadOnlyState {
  /**
   * The user cannot change this bid: an organisation viewer, or a member who
   * is not on it (the server's `canEdit === false`). Every bid-scoped write —
   * content, AI sessions, assignments, statuses, comments — is refused them
   * (`requireBidWriteAccess`).
   */
  isReadOnly: boolean
  /**
   * Whether `isReadOnly` can be trusted yet: the role is known and the bid has
   * loaded. Anything that would open an AI session waits for this.
   */
  isLoaded: boolean
  /** The one line that says why, for the notice beside a withheld control. */
  message: string
}

/**
 * "Can this user edit this bid?" — the org-viewer rule plus the bid's own
 * `canEdit`, answered the same way on every bid-scoped page.
 *
 * Reads the bid from the bids store. Bid pages load it (BidDetailsPage) and so
 * do editors opened straight from a link (BidMenu, mounted on every bid
 * route), so this hook does not fetch.
 */
export function useBidReadOnly(bidId: string | undefined): BidReadOnlyState {
  const isViewer = useAuthStore((s) => s.role === 'viewer')
  const isRoleLoaded = useAuthStore((s) => s.isRoleLoaded !== false)
  const bidCanEdit = useBidsStore((s) =>
    bidId && s.selectedBid?.id === bidId ? s.selectedBid.canEdit : null,
  )
  const isBidLoaded = useBidsStore((s) => !bidId || s.selectedBid?.id === bidId)

  const isReadOnly = isViewer || bidCanEdit === false
  return {
    isReadOnly,
    // A known viewer is read-only on every bid, so there is nothing to wait for.
    isLoaded: isRoleLoaded && (isViewer || isBidLoaded),
    message: isViewer ? `${VIEW_ONLY_MESSAGE}.` : BID_READ_ONLY_MESSAGE,
  }
}
